/**
 * StatusTemplatesDialog — workspace-level editor for the Email /
 * WhatsApp / Copy-text templates used by Planning Board's per-card
 * status shortcut buttons.
 *
 * Admins edit them once (from Planning Board → "Message templates"
 * button) and every quick-action re-renders through them. Placeholders
 * documented inline so users know what they can weave into the copy.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, MessageCircle, RotateCcw } from "lucide-react";

// Documented placeholders. Keep this list in sync with the backend
// `patch_status_templates` docstring in routes_core.py.
const PLACEHOLDERS = [
  { k: "{name}",      d: "recipient / project name" },
  { k: "{greeting}",  d: "recipient first name (falls back to 'there')" },
  { k: "{workspace}", d: "current workspace name" },
  { k: "{count}",     d: "number of tasks on the card" },
  { k: "{tasks}",     d: "numbered task list (title · status · due · project)" },
  { k: "{sender}",    d: "your own name" },
];

const DEFAULT_TPL = {
  email_subject: "Status check · {count} task{plural} · {workspace}",
  email_body:
    "Hi {greeting},\n\n" +
    "Quick status check on the tasks you're currently owning in {workspace}:\n\n" +
    "{tasks}\n\n" +
    "Could you share a short status update on each?\n\n" +
    "Thanks!\n{sender}",
  whatsapp_body:
    "Hi {greeting}, quick nudge from {workspace}:\n\n{tasks}\n\n" +
    "Please share a short status on each. Thanks!",
  text_body:
    "Hi {greeting},\n\nCurrent tasks:\n{tasks}\n\nCheers,\n{sender}",
};

export default function StatusTemplatesDialog({ open, onClose }) {
  const { activeWorkspaceId } = useAuth();
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(DEFAULT_TPL);

  useEffect(() => {
    if (!open || !activeWorkspaceId) return;
    setBusy(true);
    api.get(`/workspaces/${activeWorkspaceId}/status-templates`)
      .then((r) => {
        const t = r.data || {};
        setForm({ ...DEFAULT_TPL, ...t });
      })
      .catch((e) => toast.error(apiErrorMessage(e, "Failed to load templates")))
      .finally(() => setBusy(false));
  }, [open, activeWorkspaceId]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/workspaces/${activeWorkspaceId}/status-templates`, form);
      toast.success("Templates saved");
      onClose();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Save failed"));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[86vh] overflow-hidden flex flex-col" data-testid="status-templates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-primary" /> Message templates
          </DialogTitle>
        </DialogHeader>

        {busy ? (
          <div className="p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" /> Loading…
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto space-y-4 pr-1" data-testid="status-templates-form">
            <div className="text-[11px] text-muted-foreground border-l-2 border-primary/40 pl-2">
              Any of these placeholders can be dropped into the fields
              below and will be filled per-card at send time:
              <ul className="flex flex-wrap gap-2 mt-1">
                {PLACEHOLDERS.map((p) => (
                  <li key={p.k} className="font-mono bg-muted px-1.5 py-0.5 border border-border" title={p.d}>{p.k}</li>
                ))}
              </ul>
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email subject</Label>
              <Input value={form.email_subject}
                     onChange={(e) => setForm((f) => ({ ...f, email_subject: e.target.value }))}
                     className="h-9 rounded-none mt-1" data-testid="tpl-email-subject" />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Email body</Label>
              <Textarea value={form.email_body}
                        onChange={(e) => setForm((f) => ({ ...f, email_body: e.target.value }))}
                        className="rounded-none mt-1 font-mono text-[12px] leading-relaxed" rows={9}
                        data-testid="tpl-email-body" />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">WhatsApp body</Label>
              <Textarea value={form.whatsapp_body}
                        onChange={(e) => setForm((f) => ({ ...f, whatsapp_body: e.target.value }))}
                        className="rounded-none mt-1 font-mono text-[12px] leading-relaxed" rows={6}
                        data-testid="tpl-whatsapp-body" />
            </div>

            <div>
              <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">Copy-text body</Label>
              <Textarea value={form.text_body}
                        onChange={(e) => setForm((f) => ({ ...f, text_body: e.target.value }))}
                        className="rounded-none mt-1 font-mono text-[12px] leading-relaxed" rows={5}
                        data-testid="tpl-text-body" />
            </div>
          </div>
        )}

        <DialogFooter className="flex-shrink-0">
          <Button variant="ghost" className="rounded-none text-muted-foreground mr-auto"
                  onClick={() => setForm(DEFAULT_TPL)}
                  data-testid="tpl-reset-defaults">
            <RotateCcw className="w-3 h-3 mr-1" /> Reset to default
          </Button>
          <Button variant="ghost" className="rounded-none" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="rounded-none" data-testid="tpl-save-btn">
            {saving ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" /> Saving…</> : "Save templates"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
